import { Head, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useRef } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}

interface ExpenseData {
  id: number
  user_id: number | null
  expense_date: string | null
  paid_by: string
  paid_to: string
  preapproved_by: string
  amount: number
  notes: string
  receipt?: File | null
  receipt_filename?: string | null
}

interface PaymentMethod {
  value: string
  label: string
}

interface Props {
  activity: Activity
  expense: ExpenseData
  users: { id: number; name: string }[]
  payment_methods: PaymentMethod[]
  can_select_employee: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  activity,
  expense,
  users,
  payment_methods,
  can_select_employee,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Expenses",
      href: `/marketing_activities/${activity.id}/expenses`,
    },
    {
      title: "Edit",
      href: `/marketing_activities/${activity.id}/expenses/${expense.id}/edit`,
    },
  ]

  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, setData, put, processing, transform } = useForm<ExpenseData>({
    id: expense.id,
    user_id: expense.user_id,
    expense_date: expense.expense_date,
    paid_by: expense.paid_by,
    paid_to: expense.paid_to,
    preapproved_by: expense.preapproved_by,
    amount: expense.amount,
    notes: expense.notes,
    receipt: null,
  })

  transform((data) => ({ case_expense: data }))

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    put(`/marketing_activities/${activity.id}/expenses/${expense.id}`, {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Expense - ${activity.title}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Edit Expense</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                {can_select_employee && (
                  <div>
                    <label className={labelClass}>Employee</label>
                    <select
                      className={inputClass}
                      value={data.user_id ?? ""}
                      onChange={(e) =>
                        setData(
                          "user_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      required
                    >
                      <option value="">Select employee...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                    {errors?.user_id && (
                      <p className={errorClass}>{errors.user_id[0]}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Payment Method</label>
                  <select
                    className={inputClass}
                    value={data.paid_by}
                    onChange={(e) => setData("paid_by", e.target.value)}
                    required
                  >
                    <option value="">Select payment method...</option>
                    {payment_methods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                  {errors?.paid_by && (
                    <p className={errorClass}>{errors.paid_by[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Approved By</label>
                  <input
                    type="text"
                    className={inputClass}
                    maxLength={10}
                    value={data.preapproved_by}
                    onChange={(e) => setData("preapproved_by", e.target.value)}
                    required
                  />
                  {errors?.preapproved_by && (
                    <p className={errorClass}>{errors.preapproved_by[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={data.expense_date ?? ""}
                    onChange={(e) => setData("expense_date", e.target.value)}
                    required
                  />
                  {errors?.expense_date && (
                    <p className={errorClass}>{errors.expense_date[0]}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Paid To</label>
                  <input
                    type="text"
                    className={inputClass}
                    maxLength={50}
                    value={data.paid_to}
                    onChange={(e) => setData("paid_to", e.target.value)}
                    required
                  />
                  {errors?.paid_to && (
                    <p className={errorClass}>{errors.paid_to[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={data.amount || ""}
                    onChange={(e) =>
                      setData("amount", parseFloat(e.target.value) || 0)
                    }
                    required
                  />
                  {errors?.amount && (
                    <p className={errorClass}>{errors.amount[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Receipt</label>
                  {expense.receipt_filename && (
                    <p className="text-hpi-dark-green mb-1 text-sm">
                      Current: {expense.receipt_filename}
                    </p>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="text-hpi-dark-gray file:bg-hpi-dark-blue hover:file:bg-hpi-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      setData("receipt", file)
                    }}
                  />
                  <p className="text-hpi-med-gray mt-1 text-xs">
                    PDF, JPEG, or PNG up to 10 MB. Leave blank to keep current
                    receipt.
                  </p>
                  {errors?.receipt && (
                    <p className={errorClass}>{errors.receipt[0]}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>Explain Expense</label>
              <textarea
                className={inputClass}
                rows={4}
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                required
              />
              {errors?.notes && <p className={errorClass}>{errors.notes[0]}</p>}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() =>
                  router.get(`/marketing_activities/${activity.id}/expenses`)
                }
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Update Expense"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
